import get from 'lodash/get';
import set from 'lodash/set';
import find from 'lodash/find';
import pickBy from 'lodash/pickBy';
import mergeWith from 'lodash/mergeWith';
import isEqual from 'lodash/isEqual';
import { isProductAvailableForSale } from './utils/product.js';
import { c as colorCodesList } from './constants-LkG-Sonv.js';

var FieldType;
(function (FieldType) {
    FieldType["Color"] = "Color";
    FieldType["Dropdown"] = "Dropdown";
})(FieldType || (FieldType = {}));

var AttributeValueStatus;
(function (AttributeValueStatus) {
    AttributeValueStatus["Available"] = "available";
    AttributeValueStatus["Disabled"] = "disabled";
    AttributeValueStatus["Unavailable"] = "unavailable";
})(AttributeValueStatus || (AttributeValueStatus = {}));
const getVariantInfo = (variantsInfo, attributeCode) => find(variantsInfo, { code: attributeCode });
const getFieldType = (attributeCode) => {
    if (colorCodesList.includes(attributeCode))
        return FieldType.Color;
    return FieldType.Dropdown;
};
const getFilteredOptionValues = (option, availableAttribute) => {
    const values = (option === null || option === void 0 ? void 0 : option.values) || [];
    return values
        .filter(value => get(availableAttribute, [option.attribute_code, value.label]) !==
        AttributeValueStatus.Unavailable)
        .map(value => ({
        ...value,
        disabled: get(availableAttribute, [option.attribute_code, value.label]) ===
            AttributeValueStatus.Disabled,
    }));
};
const getOption = (option, availableAttribute) => ({
    ...option,
    values: getFilteredOptionValues(option, availableAttribute),
});
// This method checks and updates all appropriate attribute values to "available" where it is needed
// This method mutates passed "calculatedAttribute" object
const filterVariants = (variants, attributeListItem, calculatedAttribute) => {
    const attributeListItemOptions = attributeListItem.options;
    const attributeListOptionCodes = Object.keys(attributeListItemOptions);
    // Iterate through the variants to check for available attribute values
    variants.forEach(variant => {
        const attributes = (variant === null || variant === void 0 ? void 0 : variant.attributes) || [];
        // An object that contains a current variant options
        const variantOptionsMap = attributes.reduce((acc, val) => ({
            ...acc,
            [val.code]: val.label,
        }), {});
        // Remove unnecessary options from VariantOptions
        // Keep only those options which are present within AttributeList options
        // This is required for the correct options comparison
        const filteredVariantOptionsMap = pickBy(variantOptionsMap, (_, key) => attributeListOptionCodes.includes(key));
        // Compare AttributeListItem options with Variant option
        // If the options are match then change the Attribute value to "available"
        if (isEqual(attributeListItemOptions, filteredVariantOptionsMap)) {
            const { attributeCode, attributeValue } = attributeListItem;
            set(calculatedAttribute, [attributeCode, attributeValue], AttributeValueStatus.Available);
        }
    });
};
const prepareAttributeByStatusMask = (attributeList, status) => attributeList.reduce((acc, val) => {
    const attributeCode = {
        ...acc[val.attributeCode],
        [val.attributeValue]: status,
    };
    const tempAcc = {
        ...acc,
        [val.attributeCode]: attributeCode,
    };
    return tempAcc;
}, {});
const getAvailableAttribute = (product, selectedOptions) => {
    const configurableOptions = (product === null || product === void 0 ? void 0 : product.configurable_options) || [];
    const variants = (product === null || product === void 0 ? void 0 : product.variants) || [];
    // A combinations of attribute values with prepared options mask
    const attributeList = [];
    configurableOptions.forEach(configurableOption => {
        const attributeCode = configurableOption.attribute_code;
        const values = configurableOption.values || [];
        values.forEach(value => {
            const attributeValue = value.label;
            const item = {
                attributeCode,
                attributeValue,
                options: { ...selectedOptions, [attributeCode]: attributeValue },
            };
            attributeList.push(item);
        });
    });
    // Available and disabled variants (no unavailable variants)
    const availableAndDisabledVariants = variants;
    // Available variants (no unavailable and disabled variants)
    const availableVariants = variants.filter(variant => isProductAvailableForSale(variant.product.backorder_status, variant.product.stock_status));
    // This attribute object contains a predefined mask with only "unavailable" value statuses
    // This object will be mutated within the "filterVariants" method
    const allAndUnavailableAttribute = prepareAttributeByStatusMask(attributeList, AttributeValueStatus.Unavailable);
    // This attribute object contains a predefined mask with only "disabled" value statuses
    // This object will be mutated within the "filterVariants" method
    const allAndDisabledAttribute = prepareAttributeByStatusMask(attributeList, AttributeValueStatus.Disabled);
    // Iterate through all possible attribute values
    attributeList.forEach(item => {
        filterVariants(availableAndDisabledVariants, item, allAndUnavailableAttribute);
        filterVariants(availableVariants, item, allAndDisabledAttribute);
    });
    // The final proceeded attribute list which contains "available", "disabled" and "unavailable" value statuses
    const calculatedAttribute = mergeWith(allAndUnavailableAttribute, allAndDisabledAttribute, (objValue, srcValue) => {
        const onlyUnavailableAttribute = pickBy(objValue, val => val === AttributeValueStatus.Unavailable);
        return {
            ...srcValue,
            ...onlyUnavailableAttribute,
        };
    });
    return calculatedAttribute;
};
const getOptions = (product, selectedOptions) => {
    const availableAttribute = getAvailableAttribute(product, selectedOptions);
    const configurableOptions = product.configurable_options || [];
    const variantsInfo = get(product, 'variants_info.items', []);
    const options = configurableOptions.map(option => {
        var _a;
        return ({
            option: getOption(option, availableAttribute),
            variantInfoContent: (_a = getVariantInfo(variantsInfo, option.attribute_code)) === null || _a === void 0 ? void 0 : _a.content,
            fieldType: getFieldType(option.attribute_code),
            disabled: false,
        });
    });
    return options;
};
const checkIsFormFilled = (optionsList, values) => optionsList.every(option => values[option]);
const isWholeProductAvailableForSale = (productVariants) => productVariants.some(item => { var _a, _b; return isProductAvailableForSale((_a = item === null || item === void 0 ? void 0 : item.product) === null || _a === void 0 ? void 0 : _a.backorder_status, (_b = item === null || item === void 0 ? void 0 : item.product) === null || _b === void 0 ? void 0 : _b.stock_status); });

export { FieldType as F, checkIsFormFilled as c, getOptions as g, isWholeProductAvailableForSale as i };
//# sourceMappingURL=helper-k_jASZ_1.js.map
